import { Injectable, computed, signal } from '@angular/core';

const BACKOFFICE_TOKEN_KEY = 'ecl_vault_backoffice_token';
const BACKOFFICE_IDENTITY_KEY = 'ecl_vault_backoffice_identity';

export interface BackofficeIdentity {
  id: number;
  email: string;
  roles: string[];
}

@Injectable({ providedIn: 'root' })
export class BackofficeSessionService {
  private readonly tokenSignal = signal<string | null>(this.readStoredToken());
  private readonly identitySignal = signal<BackofficeIdentity | null>(this.readStoredIdentity());

  readonly token = this.tokenSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.tokenSignal());
  readonly identity = this.identitySignal.asReadonly();

  setToken(token: string): void {
    localStorage.setItem(BACKOFFICE_TOKEN_KEY, token);
    this.tokenSignal.set(token);
  }

  setIdentity(identity: BackofficeIdentity): void {
    localStorage.setItem(BACKOFFICE_IDENTITY_KEY, JSON.stringify(identity));
    this.identitySignal.set(identity);
  }

  clearToken(): void {
    localStorage.removeItem(BACKOFFICE_TOKEN_KEY);
    localStorage.removeItem(BACKOFFICE_IDENTITY_KEY);
    this.tokenSignal.set(null);
    this.identitySignal.set(null);
  }

  private readStoredToken(): string | null {
    return localStorage.getItem(BACKOFFICE_TOKEN_KEY);
  }

  private readStoredIdentity(): BackofficeIdentity | null {
    const raw = localStorage.getItem(BACKOFFICE_IDENTITY_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as BackofficeIdentity;
      if (typeof parsed?.id !== 'number' || typeof parsed?.email !== 'string' || !Array.isArray(parsed?.roles)) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }
}
